import _ from 'lodash';
import moment from 'moment';

import customers from '@/api/customers';
import documents from '@/api/documents';
import enquiries from '@/api/enquiries';
import * as types from '../mutation-types';
import buildFilter from '@/utils/customerFilterBuilder';
import { customerTermsBuilder } from '@/utils/termsBuilder';
import { saveBlobToFile } from '@/utils/fileHelpers';

const filterDefaults = {
  bool: {
    justMine: false,
    toBeCalled: false,
    closed: false,
    hotProspect: false,
    trustpilot: false,
    trustpilot_invitation_sent: false,
    financialsSent: false,
    softSearchConsent: false,
    bankDecline: false,
    brokerReferral: false,
    hasDocuments: false,
    hasEnquiryErrors: false,
    hasEnquiryFeedback: false,
    hasEnquiryQuote: false
  },
  autoSendEnquiries: false,
  notAutoSendEnquiries: false,
  eliteReferral: false,
  referral: false,
  notReferral: false,
  questionsComplete: false,
  questionsIncomplete: false,
  noEnquiries: false,
  open: false,
  owners: [],
  enquiryStatuses: [],
  productId: null,
  providerId: null,
  providerReference: null,
  journeyId: null,
  referrerType: null,
  referrer: null,
  revolvingEnquiries: false,
  newsletterSignup: false,
  ownerNotAssigned: false,
  noEmail: false,
  withEmail: false,
  noPhone: false,
  withPhone: false,
  funding: {
    minimum: 0,
    maximum: 500000000
  },
  dates: {
    from: '2016-05-01',
    to: moment().format('YYYY-MM-DD')
  },
  answers: {}
};

const defaultFields = [
  'id',
  'name',
  'funding',
  'users.name.*',
  'users.phone',
  'users.email',
  'referral',
  'referrer.name',
  'referrer.type',
  'referrer.elite',
  'enquiries.id',
  'enquiries.status.name',
  'enquiries.product.name',
  'enquiries.revolving',
  'enquiries.provider_reference',
  'referral',
  'self_referred',
  'to_be_called',
  'hot_prospect',
  'complaint_raised',
  'trustpilot',
  'financials_sent',
  'auto_send_enquiries',
  'soft_search_consent',
  'question_completion_percentage',
  'owner.id',
  'closed',
  'created_at',
  'has_documents',
  'has_enquiry_errors',
  'has_enquiry_feedback',
  'has_enquiry_quote',
  'active_journey'
];

const state = {
  page: [],
  currentPage: 1,
  pageSize: 15,
  lastPage: 1,
  total: null,
  orderBy: 'created_at',
  orderDirection: 'desc',
  search: '',
  filters: _.cloneDeep(filterDefaults),
  selected: {
    id: null,
    active_journey: {
      name: null
    },
    users: [
      {
        name: null
      }
    ]
  },
  activity: [],
  answerSet: [
    {
      id: null,
      answerable_id: null,
      question: {
        number: null,
        text: null,
        questionType: {
          name: null
        }
      },
      answer: {
        label: null
      }
    }
  ],
  documents: [],
  funded: [{ id: null, name: null }],
  loadingCustomer: true,
  hasQueryError: false,
  scrollPosition: 0
};

const getters = {
  CustomerPage: state => state.page,
  CustomerTotal: state => state.total,
  CustomerPageSize: state => state.pageSize,
  CustomerLastPage: state => state.lastPage,
  CustomerCurrentPage: state => state.currentPage,
  CustomerSearch: state => state.search,
  CustomerOrderBy: state => state.orderBy,
  CustomerDescending: state => state.orderDirection === 'desc',
  SelectedCustomer: state => state.selected,
  LoadingCustomer: state => state.loadingCustomer,
  CustomerActivity: state => state.activity,
  CustomerAnswerSet: state => state.answerSet,
  CustomerPageFilters: state => state.filters,
  CustomerFilteredAnswers: state => _.flatMap(state.filters.answers),
  CustomerDocuments: state => state.documents,
  FundedCustomers: state => state.funded,
  getAnswerById: state => id => {
    return _.find(state.selected.answerset, answer => answer.answer.id === id);
  },
  CustomerPageHasFilters: state =>
    _.isEqualWith(state.filters, filterDefaults, (objValue, othValue, key) => {
      // ignore date and funding amount when deciding if the page is filtered
      return key === 'funding' || key === 'dates' ? true : undefined;
    }) === false,
  getAnswerByQuestionId: state => questionId =>
    _.find(state.selected.answerset, answer => answer.question_id === questionId),
  CustomerPageScrollPosition: state => state.scrollPosition
};

const actions = {
  async getCustomerPage({ commit, state, rootState }, append = false) {
    commit(types.SET_CUSTOMER_QUERY_ERROR, false);

    if (append) {
      commit(types.SET_CUSTOMER_CURRENT_PAGE, state.currentPage + 1);
    } else {
      commit(types.SET_CUSTOMER_CURRENT_PAGE, 1);
      commit(types.RESET_CUSTOMER_TOTAL);
    }

    if (append && state.lastPage === 1) {
      return;
    }

    const page = state.currentPage;
    const orderBy = [{ [state.orderBy]: state.orderDirection }];
    const filters = buildFilter(state.filters, state.search);
    if (rootState.user.roles.includes('Property Broker')) {
      filters.push('active_journey.visible_to_property_brokers');
    }
    const dates = state.filters.dates;
    const search = _.isNull(state.search) ? '*' : state.search;
    const fields = defaultFields;
    const terms = customerTermsBuilder(state.filters);

    const ranges = [
      {
        funding: {
          gte: state.filters.funding.minimum,
          lte: state.filters.funding.maximum
        }
      }
    ];

    try {
      const response = await customers.getPage(
        page,
        state.pageSize,
        orderBy,
        search,
        filters,
        terms,
        ranges,
        dates,
        fields
      );

      append ? commit(types.APPEND_CUSTOMER_PAGE, response) : commit(types.SET_CUSTOMER_PAGE, response);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        commit(types.SET_CUSTOMER_QUERY_ERROR, true);
        commit(types.STOP_LOADING_CUSTOMER);
      } else {
        console.log(error);
      }
    }
  },

  downloadCustomers({ state }) {
    const orderBy = [{ [state.orderBy]: state.orderDirection }];
    const filters = { ...buildFilter(state.filters, state.search) };
    // if (rootState.user.roles.includes('Property Broker')) {
    //   filters.push('active_journey.visible_to_property_brokers');
    // }
    const dates = state.filters.dates;
    const search = _.isNull(state.search) ? '*' : state.search;
    const fields = { ...defaultFields };

    const terms = customerTermsBuilder(state.filters);

    const ranges = [
      {
        funding: {
          gte: state.filters.funding.minimum,
          lte: state.filters.funding.maximum
        }
      }
    ];

    return customers.downloadCustomersCsv({ orderBy, terms, ranges, dates, fields, filters, search });
  },

  setCustomerPageSize({ commit }, pageSize) {
    commit(types.SET_CUSTOMER_PAGE_SIZE, pageSize);
  },

  setCustomerOrderBy({ commit }, orderBy) {
    orderBy = _.isNull(orderBy) ? 'created_at' : orderBy;
    commit(types.SET_CUSTOMER_ORDER_BY, orderBy);
  },

  setCustomerDirection({ commit }, direction) {
    commit(types.SET_CUSTOMER_SORT_DIRECTION, direction);
  },

  setCustomerSearch({ commit }, search) {
    commit(types.SET_CUSTOMER_SEARCH, search);
  },

  setCustomerPageFilters({ commit }, filters) {
    return new Promise(resolve => {
      commit(types.SET_CUSTOMER_PAGE_FILTERS, filters);
      resolve();
    });
  },

  toggleCustomerPageFilter({ commit }, filter) {
    return new Promise(resolve => {
      commit(types.TOGGLE_CUSTOMER_PAGE_FILTER, filter);
      resolve();
    });
  },

  selectCustomer({ commit, dispatch, rootState }, id) {
    if (!_.isUndefined(id)) {
      dispatch('startLoadingCustomer');
      commit(types.CLEAR_PRODUCTS);
      commit(types.CLEAR_SELECTED_PARTNER);
      dispatch('clearComments').then(() => {
        const fields = [];
        if (
          rootState.user.roles.includes('Platform') ||
          rootState.user.roles.includes('Super Admin') ||
          rootState.user.roles.includes('Property Broker') ||
          rootState.user.roles.includes('Broker')
        ) {
          fields.push('*');
        } else {
          fields.push(
            'id',
            'answerset',
            'active_journey',
            'created_at',
            'funding',
            'is_limited',
            'referral',
            'referrer.name',
            'referrer.type',
            'name',
            'address',
            'credit_score',
            'business_ref',
            'registered_company_sic_codes',
            'owner',
            'users',
            'documents',
            'question_completion_percentage'
          );
        }
        customers
          .get(id, fields)
          .then(customer => {
            commit(types.SELECT_CUSTOMER, customer._source);
            if (customer._source.referrer && customer._source.referrer.id) {
              dispatch('selectPartner', customer._source.referrer.id);
            }
            dispatch('getComments', customer._id);
            dispatch('stopLoadingCustomer');
          })
          .catch(error => console.log(error));
      });
      dispatch('getAllProducts', id);
    }
  },

  clearSelectedCustomer({ commit }) {
    const selectedState = {
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        postcode: null
      },
      users: [
        {
          id: null,
          email: null,
          first_name: null,
          last_name: null,
          full_name: null,
          phone: null,
          newsletter_signup: null
        }
      ]
    };
    commit(types.SELECT_CUSTOMER, selectedState);
  },

  getActivity({ commit }, id) {
    customers
      .getActivity(id)
      .then(activity => commit(types.SET_CUSTOMER_ACTIVITY, activity.data))
      .catch(error => console.log(error));
  },

  getAnswerSet({ commit }, id) {
    customers
      .getAnswerSet(id)
      .then(answerSet => commit(types.SET_CUSTOMER_ANSWER_SET, answerSet.data))
      .catch(error => console.log(error));
  },

  async updateAnswerSet({ commit, dispatch, state }, { id, answerSet, answerId, answerText, questionId, isCritical }) {
    if (_.isUndefined(answerSet)) {
      const response = await customers.storeAnswerSet(id, questionId, answerId, answerText);
      commit(types.SET_PRODUCT_SET, { productList: response.data });
      commit(types.UPDATE_COMPLETION_PERCENTAGE, response.question_completion_percentage);
      commit(types.STORE_ANSWER_SET, response.answerset);
      return;
    }

    if (answerSet.answer.id !== answerId || (answerText !== null && answerSet.answer.answer_text !== answerText)) {
      const answerSetUpdate = {
        id: answerSet.id,
        answer_id: answerId,
        answer_text: answerText,
        question_id: questionId
      };
      const index = _.findIndex(state.selected.answerset, set => set.id === answerSet.id);
      commit(types.UPDATE_ANSWER_SET, { index, answerSet: answerSetUpdate });

      try {
        const response = await customers.updateAnswerSet(id, answerSet.id, answerId, answerText);

        if (isCritical) {
          commit(types.UPDATE_ACTIVE_JOURNEY, response.active_journey);
        }

        commit(types.SET_PRODUCT_SET, { productList: response.data });
        commit(types.UPDATE_COMPLETION_PERCENTAGE, response.question_completion_percentage);

        if (response.question_completion_percentage === 100) {
          dispatch('updateCustomer', {
            id: id,
            updates: {
              funders_revealed: true
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    }
  },

  updateAnswerSetText({ commit, state }, { id, answerSet }) {
    const index = _.findIndex(state.selected.answerset, set => set.id === id);
    commit(types.UPDATE_ANSWER_SET, { index, answerSet: answerSet });
  },

  getDocuments({ commit }, id) {
    const fields = ['documents'];
    customers
      .get(id, fields)
      .then(customer => {
        commit(types.SET_CUSTOMER_DOCUMENTS, customer._source);
      })
      .catch(error => console.log(error));
  },

  downloadDocument(context, file) {
    return new Promise((resolve, reject) => {
      customers
        .downloadDocument(file.id)
        .then(response => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveBlobToFile(blob, file.file_name);
          resolve();
        })
        .catch(error => reject(error));
    });
  },

  async deleteDocument({ commit }, document) {
    await documents.delete(document.id);
    commit(types.REMOVE_CUSTOMER_DOCUMENT, document);
  },

  startLoadingCustomer({ commit }) {
    return new Promise(resolve => {
      commit(types.START_LOADING_CUSTOMER);
      resolve();
    });
  },
  stopLoadingCustomer({ commit }) {
    return new Promise(resolve => {
      commit(types.STOP_LOADING_CUSTOMER);
      resolve();
    });
  },
  updateCustomer({ commit }, { id, updates }) {
    return customers.update(id, updates).then(response => {
      commit(types.UPDATE_CUSTOMER, response.data);
      commit(types.UPDATE_SELECTED_CUSTOMER, response.data);
    });
  },
  deleteCustomer(context, id) {
    return new Promise((resolve, reject) => {
      customers
        .delete(id)
        .then(() => resolve())
        .catch(error => reject(error));
    });
  },
  postCustomerComment({ dispatch, rootState, rootGetters }, { comment, privacy, commentParams }) {
    return new Promise((resolve, reject) => {
      let id = null;
      let type = null;
      if (_.isEmpty(commentParams)) {
        if (rootGetters.UserHasRole('Partner') && !_.isNull(rootState.customer.selected.referral_id)) {
          id = rootState.customer.selected.referral_id;
          type = 'referral';
        } else {
          resolve();
        }
      } else {
        id = commentParams.id;
        type = commentParams.type;
      }
      customers
        .postCustomerComment(id, comment, type, privacy)
        .then(comment => {
          dispatch('insertComment', comment.data);
          resolve();
        })
        .catch(error => reject(error));
    });
  },
  setCustomerPageFromDate({ commit }, from) {
    commit(types.SET_CUSTOMER_PAGE_FROM_DATE, from);
  },
  setCustomerPageToDate({ commit }, to) {
    commit(types.SET_CUSTOMER_PAGE_TO_DATE, to);
  },
  setCustomerPageFundingMinimum({ commit }, amount) {
    if (amount > state.filters.funding.maximum) {
      amount = state.filters.funding.maximum;
    }
    commit(types.SET_CUSTOMER_PAGE_FUNDING_MINIMUM, amount);
  },
  setCustomerPageFundingMaximum({ commit }, amount) {
    if (amount < state.filters.funding.minimum) {
      amount = state.filters.funding.minimum;
    }
    if (amount > 50000000) {
      amount = 50000000;
    }
    commit(types.SET_CUSTOMER_PAGE_FUNDING_MAXIMUM, amount);
  },
  getFundedCustomers({ commit }) {
    const filters = {
      whereHas: [
        {
          relation: 'enquiries.status',
          filters: {
            where: [['name', '=', 'FUNDING RELEASED']]
          }
        }
      ]
    };

    return new Promise((resolve, reject) => {
      customers
        .getFunded(filters)
        .then(response => {
          commit(types.SET_FUNDED_CUSTOMERS, response.data);
          resolve();
        })
        .catch(error => reject(error));
    });
  },

  setCustomerCurrentPage({ commit }, page) {
    commit(types.SET_CUSTOMER_CURRENT_PAGE, page);
  },

  addEnquiryStatusFilter({ commit }, status) {
    commit(types.ADD_ENQUIRY_STATUS_FILTER, status);
  },

  removeEnquiryStatusFilter({ commit }, status) {
    commit(types.REMOVE_ENQUIRY_STATUS_FILTER, status);
  },

  addOwnerFilter({ commit }, status) {
    commit(types.ADD_OWNER_FILTER, status);
  },

  removeOwnerFilter({ commit }, status) {
    commit(types.REMOVE_OWNER_FILTER, status);
  },

  addAnswerFilter({ commit }, { questionId, answerId }) {
    commit(types.ADD_ANSWER_FILTER, { questionId, answerId });
  },

  removeAnswerFilter({ commit }, { questionId, answerId }) {
    commit(types.REMOVE_ANSWER_FILTER, { questionId, answerId });
  },

  makeCustomerEnquiry({ commit, rootState }, product) {
    enquiries.makeEnquiry(rootState.customer.selected, product).then(enquiry => {
      commit(types.MAKE_CUSTOMER_ENQUIRY, enquiry);
    });
  },
  reSendCustomerEnquiry({ commit }, enquiryId) {
    enquiries.reSendEnquiry(enquiryId).then(enquiry => {
      commit(types.MAKE_CUSTOMER_ENQUIRY, enquiry);
    });
  },
  duplicateCustomerEnquiry({ commit }, { id, updates }) {
    return new Promise((resolve, reject) => {
      enquiries
        .duplicateCustomerEnquiry(id, updates)
        .then(enquiry => {
          commit(types.MAKE_CUSTOMER_ENQUIRY, enquiry);
        })
        .then(() => resolve())
        .catch(error => reject(error));
    });
  },
  storeCustomerEnquiry({ commit }, { id, updates }) {
    return new Promise((resolve, reject) => {
      enquiries
        .updateCustomerEnquiry(id, updates)
        .then(enquiry => {
          commit(types.SET_CUSTOMER_ENQUIRY, enquiry);
        })
        .then(() => resolve())
        .catch(error => reject(error));
    });
  },

  resetCustomerFilters({ commit }) {
    commit(types.RESET_CUSTOMER_FILTERS);
  },

  setCustomersScrollPosition({ commit }, position) {
    commit(types.SET_CUSTOMERS_SCROLL_POSITION, position);
  }
};

const mutations = {
  [types.SET_CUSTOMER_CURRENT_PAGE](state, page) {
    state.currentPage = page;
  },
  [types.RESET_CUSTOMER_TOTAL](state) {
    state.total = null;
  },
  [types.SET_CUSTOMER_PAGE](state, response) {
    state.page = response.hits;
    // state.total = response.total > 1000 ? 1000 : response.total;
    state.total = response.total;
    state.lastPage = Math.ceil(state.total / state.pageSize);
  },
  [types.APPEND_CUSTOMER_PAGE](state, response) {
    state.page = [...state.page, ...response.hits];
  },
  [types.SET_CUSTOMER_PAGE_SIZE](state, pageSize) {
    state.pageSize = pageSize;
  },
  [types.SET_CUSTOMER_ORDER_BY](state, orderBy) {
    state.orderBy = orderBy;
  },
  [types.SET_CUSTOMER_SORT_DIRECTION](state, direction) {
    state.orderDirection = direction ? 'desc' : 'asc';
  },
  [types.SET_CUSTOMER_PAGE_FROM_DATE](state, from) {
    state.filters.dates.from = from;
  },
  [types.SET_CUSTOMER_PAGE_TO_DATE](state, to) {
    state.filters.dates.to = to;
  },
  [types.SET_CUSTOMER_PAGE_FUNDING_MINIMUM](state, amount) {
    state.filters.funding.minimum = amount;
  },
  [types.SET_CUSTOMER_PAGE_FUNDING_MAXIMUM](state, amount) {
    state.filters.funding.maximum = amount;
  },
  [types.SET_CUSTOMER_SEARCH](state, search) {
    state.search = search;
  },
  [types.SELECT_CUSTOMER](state, customer) {
    state.selected = customer;
  },
  [types.SET_CUSTOMER_ACTIVITY](state, activity) {
    state.activity = activity;
  },
  [types.SET_CUSTOMER_ANSWER_SET](state, answerSet) {
    state.answerSet = answerSet;
  },
  [types.SET_CUSTOMER_PAGE_FILTERS](state, filters) {
    _.merge(state.filters, filters);
  },
  [types.TOGGLE_CUSTOMER_PAGE_FILTER](state, filter) {
    if (_.startsWith(filter, 'bool')) {
      filter = _.split(filter, '.', 2)[1];
      state.filters['bool'][filter] = !state.filters['bool'][filter];
    } else {
      state.filters[filter] = !state.filters[filter];
    }
  },
  [types.UPDATE_CUSTOMER](state, customer) {
    const index = state.page.findIndex(item => item._source.id === customer.id);
    if (index >= 0) {
      _.merge(state.page[index]._source, customer);
    }
  },
  [types.UPDATE_SELECTED_CUSTOMER](state, customer) {
    if (state.selected && state.selected.id === customer.id) {
      _.merge(state.selected, customer);
    }
  },
  [types.UPDATE_COMPLETION_PERCENTAGE](state, questionCompletionPercentage) {
    state.selected.question_completion_percentage = questionCompletionPercentage;
  },
  [types.UPDATE_ACTIVE_JOURNEY](state, activeJourney) {
    state.selected.active_journey = activeJourney;
  },
  [types.START_LOADING_CUSTOMER](state) {
    state.loadingCustomer = true;
  },
  [types.STOP_LOADING_CUSTOMER](state) {
    state.loadingCustomer = false;
  },
  [types.STORE_ANSWER_SET](state, answerSet) {
    state.selected.answerset.push(answerSet);
  },
  [types.UPDATE_ANSWER_SET](state, { index, answerSet }) {
    state.selected.answerset[index].answer.id = answerSet.answer_id;
    state.selected.answerset[index].answer.answer_text = answerSet.answer_text;
    state.selected.answerset[index].answer.label = answerSet.label;
    state.selected.answerset[index].answer.value = answerSet.value;
  },
  [types.POST_CUSTOMER_COMMENT](state, comment) {
    state.activity.push(comment);
  },
  [types.SET_CUSTOMER_DOCUMENTS](state, customer) {
    state.selected.documents = customer.documents;
  },
  [types.SET_FUNDED_CUSTOMERS](state, customers) {
    state.funded = customers.data;
  },
  [types.ADD_ENQUIRY_STATUS_FILTER](state, status) {
    state.filters.enquiryStatuses.push(status.id);
  },
  [types.REMOVE_ENQUIRY_STATUS_FILTER](state, status) {
    state.filters.enquiryStatuses = _.filter(state.filters.enquiryStatuses, id => id !== status.id);
  },
  [types.ADD_OWNER_FILTER](state, owner) {
    if (state.filters.owners.includes(null)) {
      state.filters.owners = [owner.id];
      state.filters.ownerNotAssigned = false;
    } else if (owner.id === null) {
      state.filters.owners = [null];
      state.filters.ownerNotAssigned = true;
    } else {
      state.filters.owners.push(owner.id);
      state.filters.ownerNotAssigned = false;
    }
  },
  [types.REMOVE_OWNER_FILTER](state, owner) {
    if (state.filters.owners.includes(null)) {
      state.filters.ownerNotAssigned = false;
    }
    state.filters.owners = _.filter(state.filters.owners, id => id !== owner.id);
  },
  [types.ADD_ANSWER_FILTER](state, { questionId, answerId }) {
    if (_.isUndefined(state.filters.answers[questionId])) {
      const filter = { [questionId]: [answerId] };
      const filters = _.clone(state.filters.answers);
      state.filters.answers = Object.assign(filters, filter);
    } else {
      state.filters.answers[questionId].push(answerId);
    }
  },
  [types.REMOVE_ANSWER_FILTER](state, { questionId, answerId }) {
    state.filters.answers[questionId] = _.filter(state.filters.answers[questionId], id => id !== answerId);
  },
  [types.MAKE_CUSTOMER_ENQUIRY](state, enquiry) {
    state.selected.enquiries.push(enquiry.data);
  },
  [types.SET_CUSTOMER_ENQUIRY](state, enquiry) {
    const index = _.findIndex(state.selected.enquiries, record => record.id === enquiry.data.id);
    _.merge(state.selected.enquiries[index], enquiry.data);
  },
  [types.RESET_CUSTOMER_FILTERS](state) {
    state.filters = _.cloneDeep(filterDefaults);
  },
  [types.SET_CUSTOMER_QUERY_ERROR](state, hasError) {
    state.hasQueryError = hasError;
  },
  [types.REMOVE_CUSTOMER_DOCUMENT](state, document) {
    const idx = state.selected.documents.findIndex(d => d === document);
    state.selected.documents.splice(idx, 1);
  },
  [types.SET_CUSTOMERS_SCROLL_POSITION](state, position) {
    state.scrollPosition = position;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
